import { FC, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import type HotTable from '@handsontable//react';
import 'handsontable/dist/handsontable.full.css';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isEmptyString, _isNil } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { ObservationTypeRow } from '@/utils/observationTypeValidation';
import type { components } from '@/generated/internal-api/openapi-types';
import { registerHOTModules } from '@/support/hot';
import SpreadSheet from '@/components/UI/SpreadSheet';
import { ScoringScaleEditor } from '@/components/UI/SpreadSheet/editors/ScoringScaleEditor';
import {
  validateObservationTypeTitle,
  validateObservationTypeLabel,
  parseScoringScale,
  formatScoringScale,
  DEFAULT_NUMERIC_SCALE_MIN,
  DEFAULT_NUMERIC_SCALE_MAX,
  type ScoringScale,
} from '@/utils/observationTypeValidation';

registerHOTModules();
ScoringScaleEditor.register();

type ObservationUpdatePayloadItem = components['schemas']['TeamObservationGlossaryUpdateItemV1.schema'];

interface BulkEditObservationTypesProps {
  observationTypes: Array<TeamGlossaryV1>;
  onSave: () => void;
  onCancel: () => void;
}

interface ValidationData {
  existing_titles: Array<string>;
  existing_labels: Array<string>;
  in_use_team_glossary_api_ids: Array<string>;
}

const BulkEditObservationTypes: FC<BulkEditObservationTypesProps> = ({ observationTypes, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<HotTable>();

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/observation-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-observation-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-edit-observation-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const validateTitle = useCallback(
    (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
      const hotInstance = hotTableRef.current?.hotInstance;
      const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];
      return validateObservationTypeTitle(value, rowIndex, validationData, observationTypes, spreadsheetData);
    },
    [validationData, observationTypes]
  );

  const validateLabel = useCallback(
    (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
      const hotInstance = hotTableRef.current?.hotInstance;
      const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];
      return validateObservationTypeLabel(value, rowIndex, validationData, observationTypes, spreadsheetData);
    },
    [validationData, observationTypes]
  );

  const initialData: Array<ObservationTypeRow> = useMemo(() => {
    return observationTypes.map((observationType) => {
      const gradingInfo = observationType.description || '';
      let scoringScale = '';

      if (observationType.type === 'numeric' && _notNil(observationType.options)) {
        const scale: ScoringScale = {
          type: 'numeric',
          minValue: Number(observationType.options.min) || DEFAULT_NUMERIC_SCALE_MIN,
          maxValue: Number(observationType.options.max) || DEFAULT_NUMERIC_SCALE_MAX,
        };
        scoringScale = formatScoringScale(scale);
      } else if (observationType.type === 'severity') {
        scoringScale = 'Severity';
      }

      return [observationType.title ?? '', observationType.label || '', scoringScale, gradingInfo];
    });
  }, [observationTypes]);

  const columns = useMemo(
    () => [
      {
        data: 0,
        title: 'Observation type',
        type: 'text',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value) || typeof value !== 'string') {
            this.comment = { value: 'Observation type is required' };
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateTitle(value, rowIndex);
          if (!validation.isValid && validation.message) {
            this.comment = { value: validation.message };
          }
          callback(validation.isValid);
        },
      },
      {
        data: 1,
        title: 'Label',
        type: 'text',
        className: 'htMiddle',
        width: 120,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value)) {
            callback(true);
            return;
          }

          if (typeof value !== 'string') {
            this.comment = { value: 'Label must be text' };
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateLabel(value, rowIndex);
          if (!validation.isValid && validation.message) {
            this.comment = { value: validation.message };
          }
          callback(validation.isValid);
        },
      },
      {
        data: 2,
        title: 'Scoring scale',
        type: 'scoring-scale',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
        // No validator needed - scoring scale is always optional
      },
      {
        data: 3,
        title: 'Grading information',
        type: 'text',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
      },
    ],
    [validationData, observationTypes]
  );

  const hotTableSettings = useMemo(
    () => ({
      data: initialData,
      colHeaders: ['Observation type', 'Label', 'Scoring scale', 'Grading information'],
      columns,
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: false,
      fillHandle: false,
      outsideClickDeselects: false,
      width: '100%',
      height: 600,
      rowHeights: 50,
      className: 'htMiddle',
      licenseKey: AppConfig.handsOnTableLicence,
      stretchH: 'all' as 'none' | 'all' | 'last' | undefined,
      afterValidate: function (isValid: boolean) {
        return isValid;
      },
      invalidCellClassName: 'htInvalid',
      commentedCellClassName: 'htCommented',
      afterRenderer: function (
        TD: HTMLElement,
        row: number,
        col: number,
        prop: string | number,
        value: any,
        cellProperties: any
      ) {
        if (cellProperties.valid === false && _notNil(cellProperties.comment?.value)) {
          const errorMessage = cellProperties.comment.value;
          TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
          TD.setAttribute('data-tooltip-content', errorMessage);
          TD.setAttribute('data-type', 'error');
          TD.title = errorMessage;
        } else if (cellProperties.readOnly) {
          const observationType = observationTypes[row];
          if (_isNil(observationType)) {
            return;
          }

          const isArchived = _notNil(observationType.archived_at);
          const isSystem = observationType.system === true;

          if (isSystem) {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This is a system observation type and cannot be modified');
            TD.setAttribute('data-type', 'info');
          } else if (isArchived) {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This observation type is archived and cannot be modified');
            TD.setAttribute('data-type', 'info');
          } else {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This observation type is in use and cannot be modified');
            TD.setAttribute('data-type', 'info');
          }
        }
      },
      cells: function (row: number, col: number) {
        const cellProperties: any = {};

        if (_notNil(observationTypes[row])) {
          const isArchived = _notNil(observationTypes[row].archived_at);
          const isSystem = observationTypes[row].system === true;

          // Archived and system observation types are fully read-only (all columns)
          if (isArchived || isSystem) {
            cellProperties.readOnly = true;
            cellProperties.className = 'htMiddle readOnly';
          } else if (_notNil(validationData)) {
            // In-use observation types only have the title column read-only
            const isInUse = validationData.in_use_team_glossary_api_ids.includes(observationTypes[row].api_id);
            if (col === 0 && isInUse) {
              cellProperties.readOnly = true;
              cellProperties.className = 'htMiddle readOnly';
            }
          }
        }

        return cellProperties;
      },
    }),
    [initialData, columns, observationTypes, validationData, validateTitle, validateLabel]
  );

  const handleSave = async (): Promise<void> => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<ObservationTypeRow>;

      const teamGlossariesPayload: Array<ObservationUpdatePayloadItem> = [];

      currentData.forEach((row, index) => {
        const [title, label, scoringScale, gradingInfo] = row;
        const observationType = observationTypes[index];

        // Skip system and archived observation types - they are read-only
        const isSystem = observationType.system === true;
        const isArchived = _notNil(observationType.archived_at);
        if (isSystem || isArchived) {
          return;
        }

        const cleanTitle = (title ?? '').toString().trim();
        const cleanLabel = (label ?? '').toString().trim();
        const cleanGradingInfo = (gradingInfo ?? '').toString().trim();

        // Parse the scoring scale
        const scale = parseScoringScale(scoringScale as string);
        let type: 'none' | 'numeric' | 'severity' = 'none';
        let options: { min: string; max: string } | null = null;

        if (_notNil(scale)) {
          if (scale.type === 'numeric') {
            type = 'numeric';
            options = {
              min: scale.minValue.toString(),
              max: scale.maxValue.toString(),
            };
          } else if (scale.type === 'severity') {
            type = 'severity';
          }
        }

        const hasLabel = !_isEmptyString(cleanLabel);
        const hasDescription = !_isEmptyString(cleanGradingInfo);

        teamGlossariesPayload.push({
          team_glossary_api_id: observationType.api_id,
          title: cleanTitle,
          type,
          label: hasLabel ? cleanLabel : null,
          description: hasDescription ? cleanGradingInfo : null,
          ...(_notNil(options) && { options }),
        });
      });

      await ApiService.call({
        endpoint: 'PATCH /api/v1/team-glossary/observations/bulk-update',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-observation-types-save' } },
      });

      successToast('Observation types updated successfully');
      onSave();
    } catch (error) {
      errorToast('Failed to update observation types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update observation types', {
          cause: error,
          slug: 'bulk-edit-observation-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white" data-testid="bulk-edit-observation-types-modal">
      <ModalHeader
        title="Edit observation types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4" data-testid="loading-validation-data">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles" data-testid="observation-types-spreadsheet">
            <SpreadSheet
              className={'mt3'}
              data={initialData}
              settings={hotTableSettings}
              addCol={false}
              innerRef={hotTableRef}
              maxRows={initialData.length}
              setInvalid={setHasValidationErrors}
              testPrefix="bulk-edit-observation-types"
            />
          </div>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Update"
        cancelBtnText="Close"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
          'data-testid': 'edit-observation-types-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          'data-testid': 'edit-observation-types-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkEditObservationTypes;
